import { generateImageSource } from '@shared/util/dom';
import { ImgHTMLAttributes, ReactElement, useEffect, useRef, useState } from 'react';

type ImageProps = ImgHTMLAttributes<HTMLImageElement>;

type UseLoadedParams = Pick<
  ImageProps,
  'crossOrigin' | 'referrerPolicy' | 'src' | 'srcSet' | 'sizes'
>;

const useLoaded = ({ crossOrigin, referrerPolicy, src, srcSet, sizes }: UseLoadedParams) => {
  const imageRef = useRef<HTMLImageElement | null>(null);
  if (!imageRef.current) {
    imageRef.current = new Image();
    if (crossOrigin) {
      imageRef.current.crossOrigin = crossOrigin;
    }
    if (referrerPolicy) {
      imageRef.current.referrerPolicy = referrerPolicy;
    }
    if (sizes) {
      imageRef.current.sizes = sizes;
    }
    if (src) {
      imageRef.current.src = src;
    }
    if (srcSet) {
      imageRef.current.srcset = srcSet;
    }
  }

  const [status, setStatus] = useState(imageRef.current.complete ? 'loaded' : 'initial');

  useEffect(() => {
    if (!src && !srcSet) {
      return;
    }

    let active = true;
    imageRef.current!.onload = () => {
      if (!active) {
        return;
      }
      setStatus('loaded');
    };
    imageRef.current!.onerror = () => {
      if (!active) {
        return;
      }
      setStatus('error');
    };

    return () => {
      active = false;
    };
  }, [src, srcSet]);

  return status;
};

type Props = Omit<ImageProps, 'src' | 'srcSet'> & {
  fallback?: ReactElement;
  fallbackOnLoading?: boolean;
  src?: string | Record<string, string> | null;
};

const AdaptiveImage = (props: Props) => {
  const { fallback, fallbackOnLoading = true, src: srcProp, ...otherProps } = props;
  const [src, srcSet] = generateImageSource(srcProp);
  const status = useLoaded({ ...otherProps, src, srcSet });

  if (status === 'error' || !src) {
    return fallback || <img {...otherProps} alt="" />;
  }

  if (status !== 'loaded') {
    return (fallbackOnLoading && fallback) || <img {...otherProps} alt="" />;
  }

  return <img {...otherProps} {...{ src, srcSet }} />;
};

export default AdaptiveImage;
